/**
 * AuthGuard — Gate that prevents unauthenticated access to the app.
 *
 * RESPONSIBILITIES:
 * - Check if a valid (non-expired) Cognito id_token exists in sessionStorage
 * - If YES → render children (the app) immediately, no flash
 * - If NO → show spinner + redirect browser to Cognito login
 * - If redirect fails → show error with retry button
 *
 * DOES NOT HANDLE:
 * - OAuth callback (?code= exchange) — that's done in index.tsx before React mounts
 * - Token refresh — token expires in 1 hour, user re-authenticates via Cognito
 *
 * WHY initial state is computed synchronously (no useEffect for token check):
 *   hasValidToken() reads sessionStorage synchronously. If the token is there
 *   and valid, the component starts in 'authenticated' state on the FIRST render.
 *   No flash of spinner. No double-render. useEffect only fires for the redirect case.
 *
 * STICTMODE SAFE:
 *   Even if useEffect fires twice (StrictMode), window.location.href = url is
 *   idempotent — the browser just navigates once. No duplicate side effects.
 */
import { useEffect, useState, ReactNode } from 'react';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import { hasValidToken, buildLoginUrl, stashPostLoginRedirect } from 'src/auth';

type GuardState = 'authenticated' | 'redirecting' | 'error';

export const AuthGuard = ({ children }: { children: ReactNode }) => {
  // Synchronous initial check — no flash if already authenticated
  const [state, setState] = useState<GuardState>(
    hasValidToken() ? 'authenticated' : 'redirecting'
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only run redirect logic if not authenticated
    if (state !== 'redirecting') return;

    // Preserve the deep link (e.g. /payment?id=... opened in a new tab) so the
    // post-login callback can return the user to it instead of the home page.
    stashPostLoginRedirect();

    // Build the Cognito login URL (async because PKCE challenge uses crypto.subtle)
    // Then navigate the browser to it. This is a full page navigation — the user
    // leaves this app entirely until Cognito redirects them back to /callback.
    buildLoginUrl()
      .then((url) => { window.location.href = url; })
      .catch((err) => {
        // Extremely rare — crypto API unavailable or config invalid
        setError(String(err));
        setState('error');
      });
  }, [state]);

  // CASE 1: Token exists and is valid → render the app
  if (state === 'authenticated') return <>{children}</>;

  // CASE 2: Redirect to Cognito failed → show error with retry
  if (state === 'error') {
    return (
      <Box padding="xxl">
        <SpaceBetween size="m" alignItems="center">
          <Alert type="error" header="Unable to redirect to login">{error}</Alert>
          <Button onClick={() => { setError(null); setState('redirecting'); }}>
            Try again
          </Button>
        </SpaceBetween>
      </Box>
    );
  }

  // CASE 3: Waiting for redirect to happen (brief spinner before browser navigates)
  return (
    <Box padding="xxl">
      <SpaceBetween size="s" alignItems="center">
        <Spinner size="large" />
        <Box variant="p" color="text-body-secondary">Redirecting to login...</Box>
      </SpaceBetween>
    </Box>
  );
};
